import { PARTNER_COCKPIT_MIN_PASSWORD_LENGTH } from '@houzy/schemas/partner-cockpit/user-management';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useState, type ReactElement, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { PasswordField } from '../../../components/PasswordField';
import { TermsConsent } from './TermsConsent';

export type SetPasswordFormProps = {
    submitLabel: string;
    isPending: boolean;
    errorMessage?: string;
    requireTosConsent?: boolean;
    onSubmit: (password: string) => void;
};

export function SetPasswordForm({
    submitLabel,
    isPending,
    errorMessage,
    requireTosConsent = false,
    onSubmit,
}: SetPasswordFormProps): ReactElement {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const mismatch = password.length > 0 && confirmation.length > 0 && password !== confirmation;
    const canSubmit =
        password.length >= PARTNER_COCKPIT_MIN_PASSWORD_LENGTH &&
        !mismatch &&
        confirmation.length > 0 &&
        (!requireTosConsent || agreedToTerms);

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!canSubmit || isPending) {
            return;
        }
        onSubmit(password);
    };

    return (
        <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {mismatch && <Alert severity="warning">{t('AUTH.SET_PASSWORD.PASSWORDS_MISMATCH')}</Alert>}
            <PasswordField
                label={t('AUTH.SET_PASSWORD.PASSWORD')}
                fullWidth
                autoComplete="new-password"
                helperText={t('AUTH.SET_PASSWORD.HINT', { min: PARTNER_COCKPIT_MIN_PASSWORD_LENGTH })}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <PasswordField
                label={t('AUTH.SET_PASSWORD.CONFIRM_PASSWORD')}
                fullWidth
                autoComplete="new-password"
                error={mismatch}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
            />
            {requireTosConsent && <TermsConsent checked={agreedToTerms} onChange={setAgreedToTerms} />}
            <Button type="submit" variant="contained" fullWidth disabled={!canSubmit || isPending}>
                {submitLabel}
            </Button>
        </Stack>
    );
}
